<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<div class="p-fluid">

  <app-warehousing-page-header activeSection="dispatch" title="Dispatches & Goods Out"
    subtitle="Post simple goods-out notes or manage planned outbound warehouse work.">
    <div actions class="flex gap-2">
      @if (canCreateGoodsOut) {
        <p-button label="Goods Out" icon="pi pi-sign-out" routerLink="/warehousing/dispatch/goods-out"></p-button>
      }
      <p-button label="New Dispatch" icon="pi pi-plus" routerLink="/warehousing/dispatch/add"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadDispatches()"></p-button>
    </div>
  </app-warehousing-page-header>

  <app-warehousing-search-panel title="Search Dispatches" [resultCount]="dispatches.length" [totalCount]="dispatches.length" (search)="applySearch()" (clear)="clearSearch()">
    <div [formGroup]="searchForm" class="formgrid grid">
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon><p-iftaLabel><input id="dispatchKeyword" pInputText formControlName="keyword"><label for="dispatchKeyword">Keyword</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon><p-iftaLabel><p-autoComplete inputId="dispatchItem" formControlName="item" [suggestions]="itemOptions" optionLabel="displayLabel" [forceSelection]="true" [showClear]="true" [delay]="300" [minLength]="1" appendTo="body" styleClass="w-full" (completeMethod)="searchItemOptions($event)"></p-autoComplete><label for="dispatchItem">Item</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon><p-iftaLabel><p-select id="dispatchWarehouse" [options]="warehouses" optionLabel="name" optionValue="id" formControlName="warehouseId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="dispatchWarehouse">Warehouse</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel><p-select id="dispatchClient" [options]="clients" optionLabel="companyName" optionValue="id" formControlName="crmClientId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="dispatchClient">Client</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon><p-iftaLabel><p-select id="dispatchSourceType" [options]="dispatchSourceOptions" optionLabel="label" optionValue="value" formControlName="sourceType" [showClear]="true" styleClass="w-full"></p-select><label for="dispatchSourceType">Source</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftaLabel><p-select id="dispatchStatus" [options]="dispatchStatusOptions" optionLabel="label" optionValue="value" formControlName="status" [showClear]="true" styleClass="w-full"></p-select><label for="dispatchStatus">Status</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <div class="grid">
          <div class="field col-6 mb-0"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon><p-iftaLabel><input id="dispatchScheduledFrom" pInputText type="date" formControlName="scheduledFrom"><label for="dispatchScheduledFrom">From</label></p-iftaLabel></p-inputGroup></div>
          <div class="field col-6 mb-0"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon><p-iftaLabel><input id="dispatchScheduledTo" pInputText type="date" formControlName="scheduledTo"><label for="dispatchScheduledTo">To</label></p-iftaLabel></p-inputGroup></div>
        </div>
      </div>
    </div>
  </app-warehousing-search-panel>

  <section class="warehousing-table-card">
    <div class="warehousing-table-header">
      <div>
        <h2>Warehouse Dispatch Directory</h2>
        <p>Review goods-out notes and planned dispatches by customer, date, reference, and status.</p>
      </div>
      <span class="warehousing-result-chip">{{ dispatches.length }} records</span>
    </div>
    <p-table [value]="dispatches" [paginator]="true" [rows]="12" dataKey="id" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" [rowsPerPageOptions]="[12, 25, 50]" [tableStyle]="{ 'min-width': '56rem' }">
      <ng-template pTemplate="header"><tr><th>Dispatch Profile</th><th>Source / Transport</th><th>Status / Quantities</th><th>Audit</th><th class="text-center">Controls</th></tr></ng-template>
      <ng-template pTemplate="body" let-dispatch>
        <tr>
          <td>
            <div class="stack-cell">
              <span class="warehousing-code-pill">{{ dispatch.dispatchNumber || '-' }}</span>
              <span class="cell-title">{{ clientName(dispatch.crmClientId) }}</span>
              <span class="cell-meta"><i class="pi pi-calendar"></i> {{ dispatch.scheduledDate || 'Not scheduled' }}</span>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title">{{ sourceLabel(dispatch) }}</span>
              <span class="cell-meta"><i class="pi pi-link"></i> {{ dispatch.sourceType || 'MANUAL' }}</span>
              <span class="cell-meta"><i class="pi pi-truck"></i> {{ transportSummary(dispatch) }}</span>
              @if (dispatch.deliveryNumber) {
                <span class="cell-meta"><i class="pi pi-map-marker"></i> {{ dispatch.deliveryNumber }}</span>
              }
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <p-tag [value]="dispatch.status" [severity]="statusSeverity(dispatch.status)"></p-tag>
              @if (showLifecycleTag(dispatch)) {
                <p-tag [value]="lifecycleStatus(dispatch)" severity="secondary"></p-tag>
              }
              <span class="cell-meta"><i class="pi pi-list"></i> {{ dispatch.lines?.length || 0 }} lines</span>
              <span class="cell-meta"><i class="pi pi-chart-bar"></i> {{ quantitiesSummary(dispatch) }}</span>
              @if (dispatch.gatedByTransferId && !dispatch.crossDockGateOpen) {
                <span class="cell-meta"><i class="pi pi-lock"></i> Waiting for inbound receipt</span>
                @if (dispatch.shipmentId) {
                  <span class="cell-meta"><i class="pi pi-truck"></i> Shipment planned; stock work remains blocked</span>
                }
              }
            </div>
          </td>
          <td><app-audit-directory-cell [stamp]="dispatch"></app-audit-directory-cell></td>
          <td class="text-center">
            <div class="warehousing-row-actions">
              <p-button icon="pi pi-eye" [text]="true" [routerLink]="['/warehousing/dispatch/view', dispatch.id]"></p-button>
              @if (canReserve(dispatch)) { <p-button label="Reserve" size="small" severity="warn" [outlined]="true" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="reserveDispatch(dispatch)"></p-button> }
              @if (canContinue(dispatch)) { <p-button label="Desktop work" size="small" severity="info" [outlined]="true" [routerLink]="['/warehousing/dispatch/view', dispatch.id]"></p-button> }
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="5"><div class="warehousing-empty-state"><i class="pi pi-search"></i><strong>No dispatches found</strong><span>No matching dispatch records.</span></div></td></tr></ng-template>
    </p-table>
  </section>
</div>
